import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Smartphone, X } from "lucide-react";

/**
 * PWA install prompt.
 *
 * Chrome / Edge / Samsung Internet on Android emit ``beforeinstallprompt``
 * which we capture and expose via a small dismissible banner. iOS Safari
 * doesn't fire the event — we detect iOS + not-standalone and show a
 * one-time hint pointing to the Share sheet instead.
 *
 * Dismissed state persists in localStorage for 7 days so we don't nag a
 * driver every shift.
 */
const DISMISS_KEY = "haulerOS.installPromptDismissedAt";
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isRecentlyDismissed() {
    try {
        const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
        return Date.now() - at < DISMISS_WINDOW_MS;
    } catch { return false; }
}

function isStandalone() {
    return window.matchMedia?.("(display-mode: standalone)").matches
        || window.navigator.standalone === true;
}

function isIOS() {
    const ua = window.navigator.userAgent || "";
    return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
}

export function InstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [showIOSHint, setShowIOSHint] = useState(false);
    const [installed, setInstalled] = useState(isStandalone());

    useEffect(() => {
        if (installed || isRecentlyDismissed()) return;

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        const onInstalled = () => {
            setDeferred(null);
            setInstalled(true);
        };

        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);

        // iOS Safari doesn't fire the event — surface manual instructions.
        if (isIOS() && !isStandalone()) {
            setShowIOSHint(true);
        }

        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, [installed]);

    const install = async () => {
        if (!deferred) return;
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") setInstalled(true);
        setDeferred(null);
    };

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* storage blocked */ }
        setDeferred(null);
        setShowIOSHint(false);
    };

    if (installed) return null;

    if (deferred) {
        return (
            <div className="rounded-lg border-2 border-primary/60 bg-primary/5 p-4 flex items-start gap-3"
                 data-testid="install-prompt-android">
                <Smartphone className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold">Install for offline use</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        Adds a home-screen icon. Routes work in dead zones once installed.
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <Button size="sm" onClick={install} data-testid="install-prompt-install-btn">
                            <Download className="w-4 h-4 mr-1" />Install app
                        </Button>
                        <Button size="sm" variant="ghost" onClick={dismiss}
                                data-testid="install-prompt-dismiss-btn">
                            Later
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (showIOSHint) {
        return (
            <div className="rounded-lg border border-border bg-secondary/50 p-3 flex items-start gap-3 text-xs"
                 data-testid="install-prompt-ios">
                <Smartphone className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                    <span className="font-medium">For offline mode: </span>
                    tap the Share icon, then <span className="font-medium">Add to Home Screen</span>.
                </div>
                <button onClick={dismiss} aria-label="Dismiss"
                        className="text-muted-foreground hover:text-foreground shrink-0">
                    <X className="w-4 h-4" />
                </button>
            </div>
        );
    }

    return null;
}
